import { HttpRequest } from "../utils";

const httpRequest = new HttpRequest();

export const variant = async (data: {
  filter: {
    query: string | null;
    categoryId: string | null;
    brandId: string | null;
    orderType: string | null;
    categories?: string[];
  };
  offset: {
    page: number;
    limit: number;
  };
  businessId: string;
}) => httpRequest.get("/erp/order/variant", data);

export type SupplierVariants = {
  supplierId: string;
  supplierName: string;
  products: any[];
  brands: any[];
};

export type MultiSupplierResult = {
  suppliers: SupplierVariants[];
  /** Suppliers whose catalogue request failed. Empty on a fully successful load. */
  failed: { supplierId: string; supplierName: string }[];
};

const CATALOG_PAGE_SIZE = 1000;
const CATALOG_MAX_PAGES = 50;

// Олон supplier-ын барааг нэг дор татах.
// allSettled, not all: one supplier's catalogue failing used to reject the whole
// batch, so a single bad response blanked the entire product list. Now the
// buyer still sees every supplier that did answer, and the caller is told which
// ones didn't. Only a total failure throws.
export const variantMultiSupplier = async (
  suppliers: { id: string; profileName?: string; partnerName?: string }[],
  categories?: string[]
): Promise<MultiSupplierResult> => {
  const settled = await Promise.allSettled(
    suppliers.map(async (supplier) => {
      // B05: page through the whole catalogue instead of one 5000-row request that
      // silently dropped the rest. Price, stock and promotion filters run on the
      // client, so every row is still needed; pages keep each response small.
      const products: any[] = [];
      const brandById = new Map<string, any>();
      for (let page = 1; page <= CATALOG_MAX_PAGES; page++) {
        const response = await httpRequest.get("/erp/order/variant", {
          filter: {
            query: "",
            categoryId: "",
            brandId: "",
            orderType: "PURCHASE",
            categories: categories,
          },
          offset: {
            page,
            limit: CATALOG_PAGE_SIZE,
          },
          businessId: supplier.id,
        });
        const rows: any[] = response.rows || [];
        products.push(...rows);
        for (const b of response.brands || []) brandById.set(b?.id ?? JSON.stringify(b), b);
        if (rows.length < CATALOG_PAGE_SIZE || products.length >= Number(response.count || 0)) break;
      }
      return {
        supplierId: supplier.id,
        supplierName: supplier.profileName || supplier.partnerName || "",
        products,
        brands: Array.from(brandById.values()),
      } as SupplierVariants;
    })
  );

  const ok: SupplierVariants[] = [];
  const failed: { supplierId: string; supplierName: string }[] = [];
  const reasons: unknown[] = [];

  settled.forEach((r, i) => {
    if (r.status === "fulfilled") {
      ok.push(r.value);
      return;
    }
    const s = suppliers[i];
    failed.push({ supplierId: s.id, supplierName: s.profileName || s.partnerName || "" });
    reasons.push(r.reason);
  });

  // Nothing came back at all — that is a load failure, not an empty catalogue.
  // Rethrow so SWR surfaces an error state instead of rendering "no products".
  if (ok.length === 0 && suppliers.length > 0) {
    throw reasons[0];
  }

  return { suppliers: ok, failed };
};

export const category = async (data: any) => {
  return httpRequest.get("/erp/order/category", data);
};

export interface ICategoryNode {
  id: string;
  name: string;
  type: string;
  parentId: string | null;
  refCode: string;
  isGoods: boolean;
  isService: boolean;
}

export interface ICategoryListResponse {
  count: number;
  rows: ICategoryNode[];
}

export const categoryList = async (params: {
  type: string;
  parentId?: string | null;
}): Promise<ICategoryListResponse> => {
  return httpRequest.get("/erp/order/category", {
    filter: {
      type: params.type,
      parentId: params.parentId || null,
    },
    offset: {
      page: 1,
      limit: 100,
    },
  });
};

// Flat category list for variant-selector-drawer (uses pricing service)
export const categories = async (): Promise<{ id: string; name: string; parentId?: string | null }[]> => {
  return httpRequest.get("/pricing/items/categories");
};

// Brand list for variant-selector-drawer (uses pricing service)
export const brands = async (): Promise<{ id: string; name: string }[]> => {
  return httpRequest.get("/pricing/items/brands");
};

// Paginated variant list for variant-selector-drawer
export const list = async ({ offset, filter, businessId }: { offset?: any; filter?: any; businessId?: string }) => {
  return httpRequest.get("/erp/order/variant", { offset, filter, businessId });
};

// Own-catalog search (no supplier relation needed — uses req.user.currentBusinessId
// on the backend). Returns the tenant's own variants.
export interface IOwnVariantRow {
  id: string;
  skuCode: string | null;
  unitName: string | null;
  item: { name?: string | null; nameMon?: string | null } | null;
}

export const items = async (params: {
  filter: { query?: string | null; isActive?: boolean | null };
  offset: { page: number; limit: number };
}): Promise<{ count: number; rows: IOwnVariantRow[] }> => {
  return httpRequest.get("/erp/items", params);
};
